import { useCallback } from 'react';
import { css } from '@emotion/react';
import { FloatingPortal, useClick, useDismiss, useFloating, useInteractions, useRole } from '@floating-ui/react';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button } from '../../button';
import { MIN_ZOOM, useImagePreviewActions } from '../provider';
import { ImagePreviewModalViewProps } from '../provider/image-preview-modal-controls';
import { controlStyles } from './constants';

export interface ImagePreviewModalProps extends ImagePreviewModalViewProps {
  heading?: React.ReactNode;
  children: React.ReactNode;
}

export const ImagePreviewModal = ({ onClose, show, heading, children }: ImagePreviewModalProps) => {
  const { changeZoom, setRotationDegree } = useImagePreviewActions();
  const zoomOutAndClose = useCallback(() => {
    setRotationDegree(0);
    changeZoom(MIN_ZOOM);
    onClose();
  }, [onClose]);

  const { refs, context } = useFloating({
    open: show,
    onOpenChange: (open) => {
      if (!open) zoomOutAndClose();
    },
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });
  const click = useClick(context);

  const { getFloatingProps } = useInteractions([dismiss, role, click]);

  if (!show) return <></>;
  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        css={[
          (theme) => css`
            height: calc(100dvh - ${theme.heightOffset}px);
            position: fixed;
            top: ${theme.heightOffset}px;
            left: 0;
            width: 100dvw;
            background: #00215280;
            display: grid;
            grid-template-rows: 44px auto 30px;
            gap: ${theme.spacing(4)};
            z-index: 1000;
            backdrop-filter: blur(${theme.spacing(0.5)});
            padding: ${theme.spacing(0, 5, 6, 5)};
          `,
          {
            [`@media (max-width: ${breakpoints.small.min}px)`]: {
              padding: theme.spacing(0, 1.5, 6, 1.5),
            },
          },
        ]}
        {...getFloatingProps()}
      >
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: theme.spacing(1.5),
            paddingBottom: 0,
          }}
        >
          {heading}
          <div
            css={[
              controlStyles,
              css`
                padding: 3px;
              `,
            ]}
          >
            <Button
              iconName={'x'}
              onClick={zoomOutAndClose}
              color='white'
              css={css`
                background: none;
                height: 24px;
                width: 24px;
                outline: none;
                &:hover {
                  background: none;
                }
              `}
            />
          </div>
        </div>
        {children}
      </div>
    </FloatingPortal>
  );
};

ImagePreviewModal.displayName = 'ImagePreviewModal';
